import type { CreateBranchFormProps } from './Form';
import type { EphemeralBranch } from '@core/store/SuperHub/Branches/utils';
import type { VersionRepresentationType } from '@readme/api-contracts/schemas/version';
import type { ComponentProps } from 'react';

import { useSuperHubStore } from '@core/store';
import { useSuperHubAppendToRootContext } from '@routes/SuperHub/components/Context';
import LazyTippy from '@ui/LazyTippy';

import {
  CreateBranchFormProvider,
  CreateBranchMetaProvider,
  useCreateBranchFormContext,
  useCreateBranchMetaContext,
} from './Context';
import CreateBranchForm from './Form';

interface CreateBranchContentProps {
  children: ComponentProps<typeof LazyTippy>['children'];
  onSuccess?: CreateBranchFormProps['onSuccess'];
}

function CreateBranchContent({ children, onSuccess }: CreateBranchContentProps) {
  const formValue = useCreateBranchFormContext();
  const { version, source } = useCreateBranchMetaContext();
  const [ephemeralBranch, updateEphemeralBranch] = useSuperHubStore(s => [
    s.branches.ephemeralBranch,
    s.branches.updateEphemeralBranch,
  ]);
  const appendToRoot = useSuperHubAppendToRootContext();

  return (
    <LazyTippy
      appendTo={() => appendToRoot || document.body}
      arrow={false}
      content={<CreateBranchForm onSuccess={onSuccess} />}
      interactive
      maxWidth={320}
      offset={[0, 4]}
      onClickOutside={() => {
        if (formValue.formState.isSubmitting) return;
        updateEphemeralBranch(null);
      }}
      placement="bottom-end"
      role="dialog"
      visible={ephemeralBranch?.base === version.name && ephemeralBranch.source === source}
      zIndex={10002} // Must be less than our Modal z-index.
    >
      {children}
    </LazyTippy>
  );
}

interface CreateBranchProps extends CreateBranchContentProps {
  source: EphemeralBranch['source'];
  version: VersionRepresentationType;
}

export default function CreateBranch({ source, version, ...rest }: CreateBranchProps) {
  return (
    <CreateBranchFormProvider>
      <CreateBranchMetaProvider source={source} version={version}>
        <CreateBranchContent {...rest} />
      </CreateBranchMetaProvider>
    </CreateBranchFormProvider>
  );
}

export { default as CreateBranchButton } from './Button';
